/**
 * Every page this app has, and how to build a link to one.
 *
 * 🔴 `PAGES` IS A PLAIN LITERAL AND MUST STAY ONE. A Python test parses it out of this file
 * and asserts that every page the server can send a teacher to actually exists here — the
 * attention list on the Overview is built server-side, so a typo in a `page` there would ship
 * as a link to nowhere and nothing else would catch it. Computing this array would make that
 * gate silently measure nothing.
 *
 * 🔴 NOTHING HAD A URL BEFORE THIS. The console was a `useState` tab index: no deep link to a
 * grade, no back button, and no way for an alarm to point at the row it is about. A report is
 * the thing a teacher wants to send to a colleague, and it was unlinkable.
 */
export const PAGES = [
  "overview",
  "assignments",
  "students",
  "teams",
  "applicants",
  "runs",
  "reviews",
  "logs",
  "health",
  "grading",
  "social",
  "teachers",
  "demo",
] as const;

export type Page = (typeof PAGES)[number];

/**
 * The grade filters of the Assignments page, in the address as `?show=`.
 *
 * 🔴 GRADES AND ENTRY STOPPED BEING PAGES (2026-10-03) AND THEN STOPPED BEING TABS (2026-10-06);
 * 🔴 AND SINCE 2026-10-13 THEY ARE AN ASSIGNMENT'S, NOT THE COHORT'S (owner: *"In review, ready and
 * so on go inside the cohort … no global needed"*). On an assignment's path `?show=` filters its
 * Handed in list; on the bare page it opens the first assignment that has any (`firstWith`).
 * ⚠️ A PLAIN LITERAL, LIKE `PAGES`: `test_overview.py` parses it and asserts every filter an
 * attention item names is one this page has.
 */
// 🔴 NO "enter" SINCE 2026-10-13 (owner: *"remove the To enter on top and that should be on
// ready"*). A mark to type into Classroom is a Ready one — a reviewed grade reads READY — so the
// Ready filter carries Copy note, Open in Classroom and Mark entered on its rows. An old
// `?show=enter` or `?view=entry` lands on Ready (`legacyMarkingHref`).
export const MARKING_FILTERS = ["review", "ready", "done", "held", "running"] as const;
export type MarkingFilter = (typeof MARKING_FILTERS)[number];

export function isMarkingFilter(s: string): s is MarkingFilter {
  return (MARKING_FILTERS as readonly string[]).includes(s);
}

/** Every filter of an assignment's Handed in list: the grade filters, then what only a listing
 *  can say — not graded here, parked, marked in Classroom, and a grade Classroom no longer lists. */
export const HANDED_FILTERS = [...MARKING_FILTERS, "ungraded", "parked", "marked", "unlisted"] as const;
export type HandedFilter = (typeof HANDED_FILTERS)[number];
export const isHandedFilter = (s: string): s is HandedFilter =>
  (HANDED_FILTERS as readonly string[]).includes(s);

/** The filter a status word belongs to — what an old `?view=grades&status=X` address meant.
 *  ⚠️ RELEASED OPENS "released or entered", the one cell that holds every released mark. */
export const FILTER_OF_STATUS: Record<string, MarkingFilter> = {
  REVIEW: "review", READY: "ready", HELD: "held", RELEASED: "done",
};

/**
 * The two pages that became views and then filters. ⚠️ THEIR ADDRESSES STILL RESOLVE — they are
 * in bookmarks, in the sidebar of every tab opened before the merge, and pasted into chat — so
 * `App.tsx` redirects them, and a caller that names one is handed the filter's own address.
 * ⚠️ `/grades` OPENED EVERY GRADE, so it lands on the page unfiltered, the closest thing there
 * is — the design's table sent it to In review, which would hide every other grade.
 */
export type RetiredPage = "grades" | "entry";

/**
 * The four tabs of the Grading page.
 *
 * ⚠️ ONE PAGE, NOT FOUR. Grader, behaviour, prompt and rubrics are four answers to one
 * question — how does this cohort get graded? — and a setting on one only makes sense beside
 * another: the usage cap is what makes the model choice affordable. The tab is in the URL so
 * an attention item can land on the one that matters.
 */
export const GRADING_TABS = ["grader", "behaviour", "prompt", "rubrics"] as const;

/**
 * The three stages of an intake, on one page — built like Teams (2026-10-13).
 *
 * 🔴 NO TEAMS TAB. Applications answer "who should join this cohort" and stop there; a team is
 * built from what an ALREADY ENROLLED student says they want to build, on the Teams page.
 * ⚠️ `form` WAS A TAB AND IS NOW TWO (Questions and Settings); the page redirects it.
 */
export const APPLICANT_TABS = ["questions", "settings", "applications"] as const;
export type ApplicantTab = (typeof APPLICANT_TABS)[number];

export function isApplicantTab(s: string): s is ApplicantTab {
  return (APPLICANT_TABS as readonly string[]).includes(s);
}

/**
 * The three stages of forming teams, on one page.
 *
 * 🔴 ITS OWN PAGE, BELOW STUDENTS — not a tab inside it. It was a tab first, which put three
 * stages of real work behind one word on a page about the roster: a teacher forming teams
 * could not link to the stage they were in, and the roster's own tabs made "Students" look
 * like a container rather than a list. The roster now shows which team each student is in,
 * which is the one fact about a person that belonged there.
 *
 * ⚠️ THE SAME SHAPE AS APPLICANTS, deliberately. Both pages answer "where is this round up
 * to?", and the answer is spread across a form's state, who has answered, and whether a plan
 * is published. The tab is in the URL so a link can point at the stage it is about.
 */
/* 🔴 `form` BECAME THREE TABS (2026-09-09). One tab held the questions, five config fields
 * and the two textareas the grouping model reads — and on a wide monitor the question column
 * grew to ~1900px while the settings sat in a 296px rail, so the narrowest fields on the page
 * were the two whose contents a model acts on. Configure, then watch, then act.
 * ⚠️ `form` IS REDIRECTED, NOT DROPPED. That address has been pasted into Discord and sits in
 * bookmarks; `TeamsPage` sends it to `questions` rather than letting it fall through to a
 * default, which would land somebody on the wrong stage with no sign anything happened. */
export const TEAM_TABS = ["questions", "settings", "grouping", "answers", "teams"] as const;
export type TeamTab = (typeof TEAM_TABS)[number];

export function isTeamTab(s: string): s is TeamTab {
  return (TEAM_TABS as readonly string[]).includes(s);
}
export type GradingTab = (typeof GRADING_TABS)[number];

/**
 * The sidebar, grouped. Cohort is the work; Operations is whether the machine is running;
 * Settings is how it is configured.
 *
 * ⚠️ A PAGE IS HIDDEN ONLY WHEN IT WOULD BE ENTIRELY EMPTY, never merely read-only. Grading
 * renders for a viewer who cannot edit and says so; hiding it would leave them unable to see
 * what the system is set to, which is a different and worse failure than a disabled control.
 * Entry, which was the one cohort page hidden without `can_release`, is a view of Assignments
 * since 2026-10-03, and the page decides whether to offer it.
 */
/**
 * 🔴 `scope` IS WHAT THE SIDEBAR DRAWS A BOX AROUND, and it is declared rather than inferred
 * from the title. The cohort picker used to sit above all twelve items, so it read as
 * governing every one of them — it governs seven. `dispatch.py:116` records what that
 * ambiguity already cost: the owner believed the grading engine was global because Config
 * presented it that way while the binder also offered it, and neither screen said *default*
 * or *override*.
 *
 * ⚠️ THE ORDER IS A COHORT'S LIFE, top to bottom: apply, enrol, group, set work, grade it,
 * enter the marks. `applicants` was last though it is the first thing that happens, and
 * `grades` sat above the roster it is about.
 *
 * ⚠️ `PAGES` IS UNTOUCHED — a Python test parses that literal, and `routes.test.ts` compares
 * these two as SETS, so reordering here is safe and dropping a page from the nav is not.
 */
export const NAV_GROUPS: { title: string; scope: "cohort" | "all"; pages: Page[] }[] = [
  // 🔴 ASSIGNMENTS IS WHERE MARKING HAPPENS NOW: Grades and Entry became its views (2026-10-03)
  // and then the strip's filters (2026-10-06), and its badge carries the in-review count the
  // Grades item used to.
  { title: "Cohort", scope: "cohort",
    pages: ["overview", "applicants", "students", "teams", "assignments"] },
  { title: "Operations", scope: "all", pages: ["runs", "reviews", "logs", "health"] },
  // 🔴 `teachers` IS RENDERED ONLY FOR AN ADMINISTRATOR, and `Layout` is where that is
  // decided — the same place `demo` is hidden without the deployment's say-so. It is listed here
  // because a page absent from this table has no label and no link, not because everybody
  // sees it. ⚠️ HIDING IT GRANTS NOTHING EITHER WAY: `deps.current_owner` re-checks the
  // list in code on every request, so this decides what is drawn and never what is allowed.
  { title: "Settings", scope: "all", pages: ["grading", "social", "teachers", "demo"] },
];

export const PAGE_LABELS: Record<Page, string> = {
  overview: "Overview",
  assignments: "Assignments",
  students: "Students",
  // ⚠️ BELOW STUDENTS AND NOT INSIDE IT. Teams are built from the roster, so they sit under
  // it in the order somebody works through a cohort — and they are three stages of work
  // rather than one view, which a tab inside another page cannot carry.
  teams: "Teams",
  // 🔴 "TEAMS" NAMED THE LAST STAGE AND HALF THE PAGE. It also holds the form a student fills
  // in, the link a teacher sends, and everybody who applied — most of which exists before any
  // team does, and all of which still matters after. "Applicants" is the noun the whole page
  // is about, and it sits beside "Students" with exactly the right distinction: students are
  // enrolled and have grades; applicants asked to be. Teams keep the word, one tab in.
  applicants: "Applicants",
  runs: "Runs",
  // Every teacher review, and how often the teacher agreed with the agent (2026-10-15).
  reviews: "Reviews",
  logs: "Logs",
  health: "Health",
  grading: "Grading",
  social: "Social activity",
  // ⚠️ "Teachers", NOT "Users" OR "Admin". Everybody this page can add is a teacher of a
  // cohort; "Users" would also describe the students who sign in to the applicant form, who
  // are a different principal in a different table and cannot be added from here at all.
  teachers: "Teachers",
  // ⚠️ "Demo cohorts", PLURAL AND NOUN-FIRST. It is a list of cohorts, not a mode the console
  // is in — "Demo" alone would read as a switch somebody had flipped on this deployment.
  demo: "Demo cohorts",
};

export function isPage(s: string): s is Page {
  return (PAGES as readonly string[]).includes(s);
}

export function isGradingTab(s: string): s is GradingTab {
  return (GRADING_TABS as readonly string[]).includes(s);
}

const c = (courseId: string) => `/c/${encodeURIComponent(courseId)}`;

/** The marking page: one assignment open (in the path) or none, a filter, and a report. They
 *  live in the address so a filtered page is a link: "every grade waiting for review on Week 2"
 *  is something a teacher can send to somebody. */
/** An open assignment's tabs (2026-10-13): Overview is the address with no `tab`. */
export const ASSIGNMENT_TABS = ["handed", "configure"] as const;
export type AssignmentTab = (typeof ASSIGNMENT_TABS)[number];
export const isAssignmentTab = (s: string): s is AssignmentTab =>
  (ASSIGNMENT_TABS as readonly string[]).includes(s);

function marking(courseId: string,
                 opts: { show?: HandedFilter | ""; cw?: string; report?: string;
                         tab?: AssignmentTab | "" } = {}): string {
  const q = new URLSearchParams();
  if (opts.tab) q.set("tab", opts.tab);
  if (opts.show) q.set("show", opts.show);
  if (opts.report) q.set("report", opts.report);
  const s = q.toString();
  const path = `${c(courseId)}/assignments${opts.cw ? `/${encodeURIComponent(opts.cw)}` : ""}`;
  return `${path}${s ? `?${s}` : ""}`;
}

export const routes = {
  /** A cohort's landing page. */
  course: (courseId: string) => `${c(courseId)}/overview`,
  /** ⚠️ A RETIRED PAGE IS ACCEPTED and answered with its view's address, so a link written
   *  before the merge goes straight there rather than through a redirect. */
  page: (courseId: string, p: Page | RetiredPage) =>
    p === "grades" || p === "entry" ? retiredPageHref(courseId, p) : `${c(courseId)}/${p}`,
  /** The marking page, filtered, with an assignment open and a report over it as asked. */
  marking,
  /** The Grading page, on one of its four tabs. */
  grading: (courseId: string, tab: GradingTab = "grader") =>
    `${c(courseId)}/grading/${tab}`,
  /** The intake, on one of its two stages. */
  applicants: (courseId: string, tab: ApplicantTab = "applications") =>
    `${c(courseId)}/applicants/${tab}`,
  /** The application form as an applicant sees it, outside the console's chrome. */
  applicantsPreview: (courseId: string) => `${c(courseId)}/applicants/form/preview`,
  /** Forming teams, on one of its three stages. */
  teams: (courseId: string, tab: TeamTab = "questions") =>
    `${c(courseId)}/teams/${tab}`,

  /** The project form as a student sees it. Its own page, because it is what a teacher
   *  opens to check before showing it to a cohort — not a panel below the settings. */
  teamsPreview: (courseId: string) => `${c(courseId)}/teams/form/preview`,
  /** One student's own page, as they see it. 🔴 IT RENDERS THEIR COMPONENTS AGAINST THE REAL
   *  PAYLOAD, which is the rule the form preview above earned the hard way. */
  studentPreview: (courseId: string, studentId: string) =>
    `${c(courseId)}/students/${encodeURIComponent(studentId)}/preview`,
  /** An assignment, open on its submissions — the address an alarm about one lands on. */
  /** One assignment: its Overview, or a tab — and a filter of its Handed in list, which is on
   *  that tab by definition. */
  assignment: (courseId: string, courseWorkId: string, tab: AssignmentTab | "" = "",
               show: HandedFilter | "" = "") =>
    marking(courseId, { cw: courseWorkId, tab: show ? "handed" : tab, show }),
  /** One grade's full report — the page a teacher sends to a colleague. */
  submission: (courseId: string, submissionId: string) =>
    `${c(courseId)}/submissions/${encodeURIComponent(submissionId)}`,
  /**
   * Where a report opens from a toast, the tray or a row: one builder, so every link to a report
   * moves together when the report's home does.
   * 🔴 OVER THE ASSIGNMENTS PAGE, AS A SIDE SHEET (2026-10-06). From that page it keeps the page's
   * own address — its filter and its open assignment — so the list under the sheet is the list
   * the reader was in; from anywhere else it opens over the page unfiltered. The standalone page
   * is `submission` above.
   */
  report: (courseId: string, submissionId: string,
           here?: { pathname: string; search: string }) => {
    if (here && (here.pathname === `${c(courseId)}/assignments`
                 || here.pathname.startsWith(`${c(courseId)}/assignments/`))) {
      const q = new URLSearchParams(here.search);
      q.set("report", submissionId);
      return `${here.pathname}?${q.toString()}`;
    }
    return marking(courseId, { report: submissionId });
  },
  /** The activity log, optionally filtered. Filters live in the query string so a view is
   *  a link: "show me every release on this assignment" is something you can send someone. */
  logs: (courseId: string, opts: { kind?: string; cw?: string; scope?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.kind) q.set("kind", opts.kind);
    if (opts.cw) q.set("cw", opts.cw);
    // ⚠️ THE COHORT FILTER IS A FILTER LIKE THE OTHERS, so it travels the same way and "every
    // release across all my cohorts" is a link somebody can send.
    if (opts.scope) q.set("scope", opts.scope);
    const s = q.toString();
    return `${c(courseId)}/logs${s ? `?${s}` : ""}`;
  },
};

/**
 * Where an address from before the one page lands (2026-10-06): `?view=grades&status=X` on the
 * filter X belongs to, `?view=entry` on the marks to type, and `?cw=` on that assignment, open.
 *
 * ⚠️ ANYTHING ELSE IN THE QUERY IS SOMEBODY'S OWN, so it is kept rather than dropped.
 * ⚠️ `?view=grades` WITH NO STATUS OPENED EVERY GRADE, and the page with no filter is the closest
 * thing there is: every assignment, each one's marks a click away.
 */
export function legacyMarkingHref(courseId: string, search: string, cwPath = ""): string {
  const q = new URLSearchParams(search);
  const view = q.get("view") ?? "";
  const status = (q.get("status") ?? "").toUpperCase();
  const asked = q.get("show") ?? "";
  const cw = cwPath || q.get("cw") || "";
  const show: MarkingFilter | "" = view === "entry" || asked === "enter" ? "ready"
    : view === "grades" ? (FILTER_OF_STATUS[status] ?? "")
    : isMarkingFilter(asked) ? asked : "";
  for (const k of ["view", "status", "cw", "show"]) q.delete(k);
  // ⚠️ ON AN ASSIGNMENT, A FILTER IS ITS HANDED IN LIST'S, so the tab is said as well.
  const base = marking(courseId, { show, cw, tab: cw && show ? "handed" : "" });
  const rest = q.toString();
  return rest ? `${base}${base.includes("?") ? "&" : "?"}${rest}` : base;
}

/** Where `/grades` and `/entry` land: the filter each became, its query string kept. */
export function retiredPageHref(courseId: string, page: RetiredPage, search = ""): string {
  const q = new URLSearchParams(search);
  q.set("view", page);
  return legacyMarkingHref(courseId, `?${q.toString()}`);
}

/** An address that still says `?view=`, `?status=` or `?cw=` — from before 2026-10-06 — or the
 *  To enter filter, from before 2026-10-13. */
export const isLegacyMarking = (search: string): boolean => {
  const q = new URLSearchParams(search);
  return q.has("view") || q.has("status") || q.has("cw") || q.get("show") === "enter";
};

/** Where an attention item's `page` (plus optional `tab`) should send the reader. */
export function attentionHref(
  courseId: string,
  item: { page: string; tab?: string; status?: string; course_work_id?: string },
): string {
  if (item.page === "assignments") {
    // ⚠️ AN ITEM ABOUT ONE ASSIGNMENT IS ABOUT ITS SUBMISSIONS — a parked one — so it opens
    // that assignment on its Handed in tab, with the list showing.
    if (item.course_work_id) return routes.assignment(courseId, item.course_work_id, "handed");
    // 🔴 A FILTER IS A `tab` OF THIS PAGE — "6 grade(s) need review" opens on those six, not on
    // every grade.
    if (item.tab && isMarkingFilter(item.tab)) return marking(courseId, { show: item.tab });
    return routes.page(courseId, "assignments");
  }
  if (item.page === "grading") {
    const t = item.tab && isGradingTab(item.tab) ? item.tab : "grader";
    return routes.grading(courseId, t);
  }
  return isPage(item.page) ? routes.page(courseId, item.page) : routes.course(courseId);
}
